import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { isCliTokenExpired } from '../../../../../../../../../domain/cli-token-access';
import { APIList } from '../../../../../../../../../typings/API.interface';
import { APIAccountCliKey } from '../../../../../../../../../typings/CliClient.interface';
import { formatDate } from '../../../../../../utils/date';
import { getCliKeyAccess } from '../../../../../common/ApiKeys/CliKeyAccess';
import Badge from '../../../../../common/base/Badge';
import Button from '../../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../../common/base/Card';
import Icon from '../../../../../common/base/Icon';
import Loading from '../../../../../common/base/Loading';
import Text from '../../../../../common/base/Text';
import Tooltip from '../../../../../common/base/Tooltip';
import CopyableField from '../../../../../common/CopyableField';
import { Div } from '../../../../../common/helpers/StyledUtils';
import { useDatePreferences } from '../../../../../hooks/useDatePreferences';
import { useNow } from './utils';
interface KeysSectionProps {
  cli_keys?: APIList<APIAccountCliKey>;
  has_error: boolean;
  page_limit: number;
  organization_names: Map<string, string>;
  project_names: Map<string, string>;
  onCreate: () => void;
  onRevoke: (cli_id: string, name: string) => void;
}

// The key comes last, like on the API Keys page. Its track fits a whole key (50 characters of 12px
// JetBrains Mono, 360px) and its three buttons, so there's no gap inside it. The name gets the
// rest. On narrow screens the key is cut short, and the copy button still copies all of it.
const GRID = {
  columns: 'minmax(120px, 1fr) minmax(0, 90px) minmax(0, 90px) minmax(0, 464px)',
  gap: 3
};

// The key, hidden until the view button is clicked, like on the org API Keys page.
const KeySecret: React.FC<{
  value: string;
}> = t0 => {
  const $ = _c(13);
  const {
    value
  } = t0;
  const [is_secret, setIsSecret] = useState(true);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      y: 2
    };
    t2 = {
      align: "center",
      gap: 1,
      grow: true
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      grow: true
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== is_secret || $[4] !== value) {
    t4 = <CopyableField mono={true} ghost={true} text_size="xs" flex={t3} value={value} secret={is_secret} reveal_on_hover={false} visible_values={4} copy_label="Copy key" />;
    $[3] = is_secret;
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = is_secret ? "view" : "dismiss";
  const t6 = is_secret ? "Show key" : "Hide key";
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsSecret(_temp);
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t5 || $[8] !== t6) {
    t8 = <Button icon={t5} ghost={true} aria-label={t6} onClick={t7} />;
    $[7] = t5;
    $[8] = t6;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t4 || $[11] !== t8) {
    t9 = <Div min_w={0} p={t1} flex={t2}>{t4}{t8}</Div>;
    $[10] = t4;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  return t9;
};
const KeysSection: React.FC<KeysSectionProps> = t0 => {
  const $ = _c(20);
  const {
    cli_keys,
    has_error,
    page_limit,
    organization_names,
    project_names,
    onCreate,
    onRevoke
  } = t0;
  const {
    timezone
  } = useDatePreferences();
  const now = useNow();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      b: 10
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      b: 4
    };
    t3 = {
      justify: "space-between",
      align: "center",
      gap: 4
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      align: "center",
      gap: 2
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Text bold={true} size="l" as="h2" m={{
      y: 0
    }}>Keys</Text>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Div flex={t4}>{t5}<Tooltip tooltip="Keys for the CLI, scripts and agents. Limit each one to an organization or projects."><Icon icon="info" muted={true} /></Tooltip></Div>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== onCreate) {
    t7 = <Div m={t2} flex={t3}>{t6}<Button primary={true} icon="add_circle" onClick={onCreate}>New key</Button></Div>;
    $[6] = onCreate;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== cli_keys || $[9] !== has_error || $[10] !== now || $[11] !== onRevoke || $[12] !== organization_names || $[13] !== page_limit || $[14] !== project_names || $[15] !== timezone) {
    t8 = has_error && !cli_keys ? <Text danger={true}>Your keys could not be loaded, please refresh the page.</Text> : <Loading require={[cli_keys]}>{() => <StyledCard overflow_hidden={true}><StyledCardSection muted={true} p={{
          x: 4
        }}><Div grid={GRID}>{["Name", "Access", "Expires", "Key"].map(_temp2)}</Div></StyledCardSection>{cli_keys.models.length === 0 && <StyledCardSection p={{
          x: 4,
          y: 3
        }}><Text muted={true}><Icon icon="info" muted={true} left={true} />No keys yet.</Text></StyledCardSection>}{cli_keys.models.map(cli_key => {
          const is_expired = isCliTokenExpired(cli_key, now);
          const access = getCliKeyAccess(cli_key, organization_names, project_names);
          return <StyledCardSection key={cli_key.id} data-testid="key-row" p={{
            x: 4
          }} grid={GRID}><Div min_w={0} p={{
              y: 3
            }} flex={{
              align: "center",
              gap: 2,
              wrap: true
            }}><Text semi={true} size="s" overflow_wrap="anywhere">{cli_key.device_name}</Text>{is_expired ? <Badge subtle={true} danger={true}>Expired</Badge> : cli_key.is_listening && <Badge subtle={true} success={true}>Active now</Badge>}</Div><Div min_w={0} flex={{
              align: "center"
            }}><Tooltip tooltip={access.details} disabled={!access.details} fill={true}><Badge neutral={true} subtle={true}><Text as="span" size="xs" ellipsis={true}>{access.label}</Text></Badge></Tooltip></Div><Div min_w={0} flex={{
              align: "center"
            }}><Text size="s" danger={is_expired} ellipsis={true}>{cli_key.expires_at ? formatDate(cli_key.expires_at, "MMM d, yyyy", timezone) : "Never"}</Text></Div><Div min_w={0} flex={{
              align: "center",
              justify: "flex-end",
              gap: 1
            }}>{cli_key.key && <KeySecret value={cli_key.key} />}<Tooltip tooltip="Revoke key" align="right"><Button ghost={true} icon="delete" aria-label="Revoke" onClick={() => onRevoke(cli_key.id, cli_key.device_name)} /></Tooltip></Div></StyledCardSection>;
        })}{cli_keys.pagination.next && <StyledCardSection p={{
          x: 4,
          y: 3
        }}><Text size="s" muted={true}>Showing your {page_limit} newest keys.</Text></StyledCardSection>}</StyledCard>}</Loading>;
    $[8] = cli_keys;
    $[9] = has_error;
    $[10] = now;
    $[11] = onRevoke;
    $[12] = organization_names;
    $[13] = page_limit;
    $[14] = project_names;
    $[15] = timezone;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <Div m={t1}>{t7}{t8}</Div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};
export default KeysSection;
function _temp(prev) {
  return !prev;
}
function _temp2(heading) {
  return <Text key={heading} muted={true} semi={true} size="xs" p={{
    y: 1
  }}>{heading}</Text>;
}